import { Building2, MapPin, Plus } from "lucide-react";
import {
  formatCurrencyDecimal,
  parseCurrencyDecimal,
} from "utils/currencyUtils";

import { Button } from "js/components/ui/button";
import { Input } from "js/components/ui/input";
import { Label } from "js/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "js/components/ui/select";
import { Separator } from "js/components/ui/separator";

import {
  PROPERTY_USE_OPTIONS,
  VALID_PROPERTY_USE_VALUES,
} from "js/constants/property-use";
import { StepComponentProps } from "../types";

export const Step1PropertySelection = ({
  formData,
  updateFormData,
  isAnimating,
  properties = [],
  selectedProperty,
  transitionToPropertyRegistration,
}: StepComponentProps) => {
  const handleAmountChange = (field: string, value: string) => {
    const numericValue = parseCurrencyDecimal(value);
    updateFormData(field, numericValue);
  };

  return (
    <div
      className={`space-y-6 transition-all duration-500 ${isAnimating ? "opacity-0 translate-x-4" : "opacity-100 translate-x-0"}`}
    >
      <div className="space-y-4">
        <div className="flex items-center gap-2 pb-2">
          <Building2 className="w-4 h-4 text-gray-700" />
          <h3 className="text-base font-semibold text-gray-900">
            Selección de Inmueble
          </h3>
        </div>
        <div className="space-y-4">
          <div>
            <Label
              htmlFor="property"
              className="text-sm font-medium text-gray-700 mb-1 block"
            >
              Inmueble *
            </Label>
            <div className="flex gap-2">
              <Select
                key={`property-select-${properties.length}`}
                value={formData.property}
                onValueChange={(value) => {
                  updateFormData("property", value);
                  // Auto-populate fields when a property is selected
                  const property = properties.find((p) => p.id === value);
                  if (property) {
                    // Auto-fill property use if available and valid
                    // property.uso contains the usage ID from the API
                    if (
                      property.uso &&
                      VALID_PROPERTY_USE_VALUES.includes(property.uso)
                    ) {
                      updateFormData("propertyUse", property.uso);
                    } else {
                      // Clear invalid property use value
                      updateFormData("propertyUse", "");
                    }
                    // Auto-fill rental price if available
                    if (property.rental_price !== undefined) {
                      updateFormData(
                        "rentAmount",
                        String(property.rental_price),
                      );
                    }
                    // Auto-fill maintenance price - always update, even if 0
                    if (property.maintenance_price !== undefined) {
                      updateFormData(
                        "maintenanceAmount",
                        String(property.maintenance_price),
                      );
                    } else {
                      // Clear the field if maintenance_price is not defined
                      updateFormData("maintenanceAmount", "");
                    }
                  }
                }}
              >
                <SelectTrigger className="flex-1 border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900">
                  <SelectValue placeholder="Selecciona un inmueble" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                variant="outline"
                className="border-gray-900 text-gray-900 hover:bg-[#0f5ef7] hover:text-white hover:border-[#0f5ef7]"
                onClick={transitionToPropertyRegistration}
              >
                <Plus className="w-4 h-4 mr-1" /> Registrar
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label
                htmlFor="propertyUse"
                className="text-sm font-medium text-gray-700 mb-1 block"
              >
                Uso del Inmueble *
              </Label>
              <Select
                value={formData.propertyUse}
                onValueChange={(value) => updateFormData("propertyUse", value)}
                disabled={!formData.property}
              >
                <SelectTrigger className="border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed">
                  <SelectValue placeholder="Selecciona el uso" />
                </SelectTrigger>
                <SelectContent>
                  {PROPERTY_USE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label
                htmlFor="rentAmount"
                className="text-sm font-medium text-gray-700 mb-1 block"
              >
                Monto de la Renta *
              </Label>
              <div className="relative">
                <Input
                  id="rentAmount"
                  type="text"
                  placeholder="$12,000.00"
                  value={formatCurrencyDecimal(formData.rentAmount)}
                  onChange={(e) =>
                    handleAmountChange("rentAmount", e.target.value)
                  }
                  disabled={!formData.property}
                  className="border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed pr-12"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <span className="text-gray-500 text-sm font-medium">MXN</span>
                </div>
              </div>
            </div>
            <div>
              <Label
                htmlFor="maintenanceAmount"
                className="text-sm font-medium text-gray-700 mb-1 block"
              >
                Monto del Mantenimiento
              </Label>
              <div className="relative">
                <Input
                  id="maintenanceAmount"
                  type="text"
                  placeholder="$3,000.00"
                  value={formatCurrencyDecimal(formData.maintenanceAmount)}
                  onChange={(e) =>
                    handleAmountChange("maintenanceAmount", e.target.value)
                  }
                  disabled={!formData.property}
                  className="border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed pr-12"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <span className="text-gray-500 text-sm font-medium">MXN</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Separator className="my-4" />
      <div className="space-y-4">
        <div className="flex items-center gap-2 pb-2">
          <MapPin className="w-4 h-4 text-gray-700" />
          <h3 className="text-base font-semibold text-gray-900">
            Información del Inmueble
          </h3>
        </div>
        {selectedProperty ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Dirección
                </dt>
                <dd className="text-sm text-gray-900 font-medium">
                  {selectedProperty.direccion}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Alias
                </dt>
                <dd className="text-sm text-gray-900 font-medium">
                  {selectedProperty.alias}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Colonia
                </dt>
                <dd className="text-sm text-gray-900 font-medium">
                  {selectedProperty.colonia}
                </dd>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="text-center text-gray-500 text-sm py-2">
              Selecciona o registra un inmueble para ver su información
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
